import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, idr, fmtNum } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { Users, UserCheck, UserX, Wallet, ShoppingBag, Smartphone, TrendingUp, Award, Sparkles, RefreshCw, AlertTriangle, Target, Clock, CheckCircle2, DollarSign, UsersRound, ArrowUpRight, ArrowDownRight, Minus, EyeOff, HandCoins, Receipt, History, Ticket, Printer } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { hariIniISO, hariIniDate, isoDariDate, formatTanggal } from "@/lib/tanggal";
import LoadingState from "@/components/ui/loading-state";
import { useDataQuery } from "@/hooks/useDataQuery";
import { rutaUntukAktivitas } from "@/lib/rutaAktivitas";

export const COLORS = ["#059669", "#0F172A", "#F59E0B", "#3B82F6", "#EF4444", "#8B5CF6", "#14B8A6", "#F97316"];

// Diekspor supaya Layout.jsx bisa prefetch query yang sama persis saat kursor
// hover menu "Dashboard" cukup lama — satu-satunya sumber kebenaran untuk key ini.
export const DASHBOARD_QK = ["dashboard"];
export const DASHBOARD_FINANCE_QK = ["dashboard-finance"];
export const DASHBOARD_AKTIVITAS_QK = ["dashboard-aktivitas"];

export const Kpi = ({ icon: Icon, label, value, tint = "emerald", delay = 0, tid, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
    data-testid={tid}
    className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm"
  >
    <div className="flex items-start justify-between mb-3 md:mb-6">
      <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600`}>
        <Icon className="w-4 h-4 md:w-5 md:h-5" />
      </div>
    </div>
    <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
    <p className="font-display text-xl md:text-2xl font-bold text-slate-900 truncate">{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </motion.div>
);

const ICON_MAP = { alert: AlertTriangle, target: Target, users: UsersRound, clock: Clock, money: DollarSign, check: CheckCircle2 };
const PRI_STYLE = {
  high:   { badge: "bg-rose-100 text-rose-700 border-rose-200",     ring: "text-rose-600 bg-rose-50",       label: "PRIORITAS TINGGI" },
  medium: { badge: "bg-amber-100 text-amber-800 border-amber-200",  ring: "text-amber-600 bg-amber-50",     label: "PERLU DILIHAT" },
  low:    { badge: "bg-emerald-100 text-emerald-700 border-emerald-200", ring: "text-emerald-600 bg-emerald-50", label: "KABAR BAIK" },
};

function AIInsightCard() {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("yesterday"); // yesterday | last_week | last_month | custom
  const [customStart, setCustomStart] = useState(() => {
    const d = hariIniDate(); d.setDate(d.getDate() - 7);
    return isoDariDate(d);
  });
  const [customEnd, setCustomEnd] = useState(() => {
    const d = hariIniDate(); d.setDate(d.getDate() - 1);
    return isoDariDate(d);
  });

  const fetchInsight = useCallback(async (refresh = false, p = period, cs = customStart, ce = customEnd) => {
    (refresh ? setRefreshing : setLoading)(true);
    try {
      let url = `/ai/dashboard-insight?period=${p}${refresh ? "&refresh=true" : ""}`;
      if (p === "custom") url += `&start=${cs}&end=${ce}`;
      const r = await api.get(url);
      setInsight(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat AI Insight");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => {
    // auto-fetch untuk preset periods; custom butuh user klik "Terapkan"
    if (period !== "custom") fetchInsight(false, period);
  }, [period, fetchInsight]);

  const PERIODS = [
    { key: "yesterday",  label: "Kemarin",     desc: "1 hari yg lalu" },
    { key: "last_week",  label: "Minggu Lalu", desc: "Sen–Min lalu" },
    { key: "last_month", label: "Bulan Lalu",  desc: "Bulan penuh" },
    { key: "custom",     label: "Custom",      desc: "Pilih rentang tanggal bebas" },
  ];

  const items = insight?.items || [];
  const trend = insight?.trend_7d || [];
  const genStr = insight?.generated_at ? formatTanggal(insight.generated_at, { denganJam: true }) : "-";

  // Trend stats
  const trendTotal = trend.reduce((a, t) => a + (t.omzet || 0), 0);
  const trendAvg = trend.length ? trendTotal / trend.length : 0;
  const first3 = trend.slice(0, 3).reduce((a, t) => a + (t.omzet || 0), 0);
  const last3 = trend.slice(-3).reduce((a, t) => a + (t.omzet || 0), 0);
  const deltaPct = first3 > 0 ? Math.round(((last3 - first3) / first3) * 100) : 0;
  const trendUp = deltaPct >= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid="ai-insight-card"
      className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 border border-emerald-700/40 rounded-2xl p-5 md:p-6 shadow-lg mb-6 md:mb-8"
    >
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">AI Insight · GPT-5.2 · Laporan {insight?.period_label || "kemarin"}</p>
              <h3 className="font-display text-lg md:text-xl font-bold text-white">Apa yang perlu di-fix hari ini?</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">
                Update: {genStr}
                {insight?.period_start && (
                  <> · <span className="text-emerald-400">{insight.period_start} → {insight.period_end}</span></>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Period selector — dibungkus overflow-x-auto + flex-nowrap (bukan overflow-hidden)
                supaya tombol "Custom" tidak terpotong/tak terjangkau di layar HP sempit. Kartu
                pembungkusnya (motion.div di atas) sengaja overflow-hidden untuk mengunci glow
                background, jadi baris tombol ini butuh area scroll horizontal sendiri. */}
            <div className="max-w-full inline-flex flex-nowrap items-center gap-0.5 rounded-lg bg-white/5 border border-white/10 overflow-x-auto px-1"
              data-testid="ai-insight-period-selector">
              {PERIODS.map(p => (
                <button key={p.key} onClick={() => setPeriod(p.key)}
                  data-testid={`ai-insight-period-${p.key}`}
                  title={p.desc}
                  className={`shrink-0 whitespace-nowrap h-8 px-3 my-1 rounded-md text-[11px] font-medium transition-colors ${
                    period === p.key ? "bg-emerald-500 text-white" : "text-slate-300 hover:bg-white/10"
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
            {period === "custom" && (
              <div className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-lg px-2 py-1" data-testid="ai-insight-custom-range">
                <input type="date" value={customStart} max={customEnd}
                  onChange={e => setCustomStart(e.target.value)}
                  data-testid="ai-insight-custom-start"
                  className="bg-transparent text-white text-[11px] outline-none [color-scheme:dark]" />
                <span className="text-slate-400 text-[11px]">→</span>
                <input type="date" value={customEnd} min={customStart}
                  max={hariIniISO()}
                  onChange={e => setCustomEnd(e.target.value)}
                  data-testid="ai-insight-custom-end"
                  className="bg-transparent text-white text-[11px] outline-none [color-scheme:dark]" />
                <button onClick={() => fetchInsight(true, "custom", customStart, customEnd)}
                  disabled={loading || refreshing || !customStart || !customEnd}
                  data-testid="ai-insight-custom-apply"
                  className="ml-1 h-6 px-2 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-semibold disabled:opacity-50">
                  Terapkan
                </button>
              </div>
            )}
            <button
              onClick={() => fetchInsight(true, period, customStart, customEnd)}
              disabled={refreshing || loading}
              data-testid="ai-insight-refresh"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium disabled:opacity-50 border border-white/10"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Menyusun..." : "Refresh"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[1, 2].map(i => (
              <div key={i} className="h-24 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : insight?.unavailable ? (
          <div className="text-sm text-slate-300" data-testid="ai-insight-belum-aktif">
            <p className="font-medium text-slate-200">Fitur AI belum aktif.</p>
            <p className="mt-1 text-slate-400">{insight.reason}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-slate-300">Belum ada insight — coba refresh.</div>
        ) : (
          <>
            {insight?.headline && (
              <p className="text-sm md:text-base text-slate-100 mb-4 italic" data-testid="ai-insight-headline">
                &ldquo;{insight.headline}&rdquo;
              </p>
            )}

            {/* 7-day omzet trend sparkline */}
            {trend.length > 0 && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4" data-testid="ai-insight-trend">
                <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Trend Omzet · 7 Hari</span>
                    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${trendUp ? "text-emerald-400" : "text-rose-400"}`}>
                      {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {trendUp ? "+" : ""}{deltaPct}%
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Rata-rata: <span className="text-slate-200 font-mono">{idr(Math.round(trendAvg))}</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={56}>
                  <AreaChart data={trend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34D399" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#34D399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Tooltip
                      cursor={{ stroke: "#34D399", strokeWidth: 1, strokeDasharray: "3 3" }}
                      contentStyle={{ borderRadius: 8, fontSize: 11, backgroundColor: "#0F172A", border: "1px solid #10B981", color: "#fff" }}
                      formatter={(v) => [idr(v), "Omzet"]}
                      labelFormatter={(l) => `Tgl ${l}`}
                    />
                    <Area type="monotone" dataKey="omzet" stroke="#34D399" strokeWidth={2} fill="url(#sparkGrad)" dot={{ r: 2, fill: "#34D399" }} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex justify-between mt-0.5 px-1">
                  {trend.map((t, i) => (
                    <span key={i} className="text-[9px] text-slate-500 font-mono">{t.label}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.slice(0, 4).map((it, i) => {
                const Icon = ICON_MAP[it.icon] || AlertTriangle;
                const st = PRI_STYLE[it.priority] || PRI_STYLE.medium;
                return (
                  <div key={i} data-testid={`ai-insight-item-${i}`}
                    className="bg-white/95 rounded-xl p-3.5 border border-white/20 shadow-sm">
                    <div className="flex items-start gap-2.5">
                      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${st.ring}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${st.badge}`}>{st.label}</span>
                        </div>
                        <p className="font-display font-semibold text-sm text-slate-900 leading-tight">{it.title}</p>
                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">{it.detail}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

/* ---- Helper components (module-level for stable identity) ---- */
function WowTrendPill({ pct, up, flat }) {
  const Icon = flat ? Minus : (up ? ArrowUpRight : ArrowDownRight);
  const cls = flat
    ? "bg-slate-100 text-slate-600 border-slate-200"
    : up
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : "bg-rose-100 text-rose-700 border-rose-200";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {flat ? "0%" : `${up ? "+" : ""}${pct}%`}
    </span>
  );
}

function WowMiniBars({ tw_daily = [], lw_daily = [] }) {
  const twMax = Math.max(...tw_daily.map(d => d.omzet || 0), 1);
  const lwMax = Math.max(...lw_daily.map(d => d.omzet || 0), 1);
  const maxVal = Math.max(twMax, lwMax);
  const labels = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
  return (
    <div className="mt-3 flex items-end justify-between gap-1.5 h-16">
      {labels.map((lbl, i) => {
        const twVal = tw_daily[i]?.omzet || 0;
        const lwVal = lw_daily[i]?.omzet || 0;
        const twH = (twVal / maxVal) * 56;
        const lwH = (lwVal / maxVal) * 56;
        const hasThisWeek = i < tw_daily.length;
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end justify-center gap-0.5 h-14">
              <div className="w-1/2 bg-slate-300/70 rounded-t-sm" style={{ height: `${lwH}px` }} title={`Minggu Lalu: ${lwVal.toLocaleString("id-ID")}`} />
              <div className={`w-1/2 rounded-t-sm ${hasThisWeek ? "bg-emerald-500" : "bg-slate-100"}`} style={{ height: `${hasThisWeek ? twH : 0}px` }} title={`Minggu Ini: ${twVal.toLocaleString("id-ID")}`} />
            </div>
            <span className="text-[9px] text-slate-500">{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ================= Attendance Week-over-Week Comparison Card ================= */
function AttWowRow({ label, twVal, lwVal, pct, upBad, icon: I, tint }) {
  const flat = pct === 0;
  const up = pct > 0;
  const isGood = flat ? null : (upBad ? !up : up);
  const cls = flat ? "text-slate-500" : isGood ? "text-emerald-700" : "text-rose-700";
  const Arrow = flat ? Minus : (up ? ArrowUpRight : ArrowDownRight);
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-4" data-testid={`att-wow-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <I className={`w-3.5 h-3.5 text-${tint}-600`} />
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        </div>
        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${cls}`}>
          <Arrow className="w-3 h-3" />
          {flat ? "0%" : `${up ? "+" : ""}${pct}%`}
        </span>
      </div>
      <p className="font-display text-2xl font-bold text-slate-900">{twVal}</p>
      <p className="text-xs text-slate-500 mt-1">
        Minggu lalu: <span className="font-mono text-slate-700">{lwVal}</span>
      </p>
    </div>
  );
}

export function AttendanceWowCard({ att }) {
  const tw = att.this_week || {};
  const lw = att.last_week || {};
  const d = att.delta || {};

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
      data-testid="attendance-wow-card"
      className="mb-6 md:mb-8 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm"
    >
      <div className="mb-4">
        <h3 className="font-display font-bold text-slate-900 text-base md:text-lg inline-flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-sky-600" />
          Absensi Minggu Ini vs Minggu Lalu
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Trend disiplin tim · perbandingan hari kerja yang sama · headcount aktif: <b>{Math.round((tw.expected || 0) / Math.max(1, (att.this_week?.n_hadir + tw.n_alpa) > 0 ? 1 : 1))}</b>
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <AttWowRow label="Hadir" twVal={tw.n_hadir || 0} lwVal={lw.n_hadir || 0}
          pct={d.hadir_pct || 0} upBad={false} icon={UserCheck} tint="emerald" />
        <AttWowRow label="Telat" twVal={tw.n_telat || 0} lwVal={lw.n_telat || 0}
          pct={d.telat_pct || 0} upBad={true} icon={Clock} tint="amber" />
        <AttWowRow label="Lembur" twVal={tw.n_lembur || 0} lwVal={lw.n_lembur || 0}
          pct={d.lembur_pct || 0} upBad={false} icon={TrendingUp} tint="sky" />
        <AttWowRow label="Alpa" twVal={tw.n_alpa || 0} lwVal={lw.n_alpa || 0}
          pct={d.alpa_pct || 0} upBad={true} icon={UserX} tint="rose" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-slate-500 mb-1">Total menit telat</p>
          <p className="font-mono font-semibold text-slate-900">{tw.total_late_min || 0}m <span className="text-slate-400">vs {lw.total_late_min || 0}m minggu lalu</span></p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
          <p className="text-slate-500 mb-1">Total menit lembur</p>
          <p className="font-mono font-semibold text-slate-900">{tw.total_ot_min || 0}m <span className="text-slate-400">vs {lw.total_ot_min || 0}m minggu lalu</span></p>
        </div>
      </div>
    </motion.section>
  );
}

/* ================= Week-over-Week Comparison Card ================= */
function WowCompareCard({ wow }) {
  const tw = wow.this_week || {};
  const lw = wow.last_week || {};
  const d = wow.delta || {};
  const omzetPct = d.omzet_pct || 0;
  const countPct = d.count_pct || 0;
  const acqPct = d.acq_pct || 0;
  const omzetUp = omzetPct > 0;
  const omzetFlat = omzetPct === 0;
  const countUp = countPct > 0;
  const countFlat = countPct === 0;
  const acqUp = acqPct > 0;
  const acqFlat = acqPct === 0;

  const fmtDate = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso + "T00:00:00");
    return dt.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  };
  const twRange = `${fmtDate(tw.start)} – ${fmtDate(tw.end)}`;
  const lwSameEnd = (() => {
    if (!lw.start || !tw.end || !tw.start) return "";
    const twStartDt = new Date(tw.start + "T00:00:00");
    const twEndDt = new Date(tw.end + "T00:00:00");
    const daysDiff = Math.round((twEndDt - twStartDt) / 86400000);
    const lwStartDt = new Date(lw.start + "T00:00:00");
    const sameEnd = new Date(lwStartDt.getTime() + daysDiff * 86400000);
    return sameEnd.toLocaleDateString("id-ID", { day: "numeric", month: "short" });
  })();
  const lwRange = lw.start ? `${fmtDate(lw.start)} – ${lwSameEnd}` : "";

  const idrCompact = (v) => {
    v = Number(v || 0);
    if (v >= 1e9) return `Rp ${(v / 1e9).toFixed(2)}M`;
    if (v >= 1e6) return `Rp ${(v / 1e6).toFixed(1)}jt`;
    if (v >= 1e3) return `Rp ${(v / 1e3).toFixed(0)}rb`;
    return `Rp ${v.toLocaleString("id-ID")}`;
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      data-testid="wow-compare-card"
      className="mb-6 md:mb-8 bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="font-display font-bold text-slate-900 text-base md:text-lg inline-flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Minggu Ini vs Minggu Lalu
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Perbandingan hari yang sama · <span className="font-medium text-slate-700">{twRange}</span> vs <span className="text-slate-500">{lwRange}</span>
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm" /> Minggu Ini</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded-sm" /> Minggu Lalu</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Omzet compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50/60 to-white p-4" data-testid="wow-omzet-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Omzet Penjualan</p>
            <WowTrendPill pct={omzetPct} up={omzetUp} flat={omzetFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-omzet-tw">{idrCompact(tw.omzet || 0)}</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{idrCompact(lw.omzet_same || 0)}</span>
          </p>
          <p className="text-[11px] mt-1">
            Selisih:
            <span className={`ml-1 font-mono font-semibold ${omzetFlat ? "text-slate-600" : omzetUp ? "text-emerald-700" : "text-rose-700"}`}>
              {(d.omzet_abs || 0) >= 0 ? "+" : ""}{idrCompact(d.omzet_abs || 0)}
            </span>
          </p>
          <WowMiniBars tw_daily={tw.daily || []} lw_daily={lw.daily || []} />
        </div>

        {/* Transaksi compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-sky-50/60 to-white p-4" data-testid="wow-count-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Jumlah Transaksi</p>
            <WowTrendPill pct={countPct} up={countUp} flat={countFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-count-tw">{fmtNum(tw.count || 0)} tx</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{fmtNum(lw.count_same || 0)} tx</span>
          </p>
          <p className="text-[11px] mt-1">
            Selisih:
            <span className={`ml-1 font-mono font-semibold ${countFlat ? "text-slate-600" : countUp ? "text-emerald-700" : "text-rose-700"}`}>
              {(d.count_abs || 0) >= 0 ? "+" : ""}{fmtNum(d.count_abs || 0)} tx
            </span>
          </p>
          <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
            <span>Rata-rata / hari</span>
            <span className="font-mono text-slate-700">
              {fmtNum(Math.round((tw.count || 0) / Math.max((tw.daily || []).length, 1)))} vs {fmtNum(Math.round((lw.count_same || 0) / Math.max((tw.daily || []).length, 1)))}
            </span>
          </div>
        </div>

        {/* Akuisisi compare */}
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50/60 to-white p-4" data-testid="wow-acq-block">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Akuisisi Unit</p>
            <WowTrendPill pct={acqPct} up={acqUp} flat={acqFlat} />
          </div>
          <p className="font-display text-2xl font-bold text-slate-900" data-testid="wow-acq-tw">{fmtNum(tw.acq_count || 0)} unit</p>
          <p className="text-xs text-slate-500 mt-1">
            Minggu lalu (hari sama): <span className="font-mono text-slate-700">{fmtNum(lw.acq_count_same || 0)} unit</span>
          </p>
          <div className={`mt-2 rounded-lg p-2 text-xs ${acqFlat ? "bg-slate-50 text-slate-600" : acqUp ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>
            {acqFlat
              ? "Akuisisi stabil dibanding minggu lalu."
              : acqUp
                ? `Naik ${acqPct}% dari minggu lalu — momentum baik.`
                : `Turun ${Math.abs(acqPct)}% dari minggu lalu — perlu dorong akuisisi.`}
          </div>
        </div>
      </div>

      {/* Overall verdict banner */}
      <div className={`mt-4 rounded-xl p-3 text-sm border flex items-center gap-2 ${
        omzetFlat ? "bg-slate-50 border-slate-200 text-slate-700" :
        omzetUp   ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                    "bg-rose-50 border-rose-200 text-rose-800"
      }`}>
        {omzetFlat ? <Minus className="w-4 h-4" /> : omzetUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        <span data-testid="wow-verdict">
          {omzetFlat
            ? "Omzet sama dengan minggu lalu di periode yang sama."
            : omzetUp
              ? `Omzet naik ${omzetPct}% (${idrCompact(d.omzet_abs || 0)}) dibanding minggu lalu di periode yang sama.`
              : `Omzet turun ${Math.abs(omzetPct)}% (${idrCompact(d.omzet_abs || 0)}) dibanding minggu lalu di periode yang sama.`}
        </span>
      </div>
    </motion.section>
  );
}

/** Format timestamp log aktivitas (WIB ISO string) ke label ringkas id-ID. */
function fmtWaktuAktivitas(iso) {
  if (!iso) return "-";
  try {
    return formatTanggal(iso, { denganJam: true });
  } catch {
    return iso;
  }
}

/** Laporan antrian dicetak (brief owner 15 Sep 2026, G7).
 *
 *  "Dicetak" = tombol Cetak Tiket DITEKAN. Peramban tidak bisa tahu kertasnya
 *  benar-benar keluar, jadi labelnya tidak mengaku lebih dari itu. "Terbit"
 *  selalu ditampilkan berdampingan karena keduanya memang berbeda: FL yang
 *  menerbitkan nomor untuk dirinya sendiri cukup mengetik kodenya. */
function SeksiAntrian({ antrian, bolehBuka }) {
  const h = antrian.hari_ini || {};
  const b = antrian.bulan_ini || {};
  const perCabang = antrian.per_cabang || [];
  const sejak = antrian.penanda_cetak_sejak;
  // Tiket yang terbit sebelum penanda cetak ada terbaca "belum dicetak".
  // Tanpa catatan ini, angka dicetak yang rendah di awal terlihat seperti
  // tiket yang tidak dicetak, padahal cuma belum tercatat.
  const perluCatatanCetak = b.terbit > 0 && (!sejak || sejak > antrian.dari);
  return (
    <section className="mb-8 md:mb-10" data-testid="dash-antrian">
      <div className="flex items-center justify-between gap-2 mb-4">
        <h2 className="font-display text-base md:text-lg font-bold text-slate-900">Antrian Pelanggan</h2>
        <div className="flex items-center gap-2">
          {bolehBuka && (
            <Link to="/antrian?tab=antrean" data-testid="dash-antrian-buka"
                  className="text-[11px] md:text-xs text-emerald-700 hover:underline">Buka Antrian</Link>
          )}
          <span className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">Bulan Ini</span>
        </div>
      </div>
      {antrian.pesan_cabang && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="dash-antrian-pesan-cabang">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">{antrian.pesan_cabang}</p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-4">
        <Kpi tid="kpi-antrian-dicetak-hari-ini" icon={Printer} label="Tiket Dicetak Hari Ini" value={fmtNum(h.dicetak || 0)}
             sub={`dari ${fmtNum(h.terbit || 0)} nomor terbit${h.cetak_ulang ? ` · ${fmtNum(h.cetak_ulang)} cetak ulang` : ""}`}
             tint="emerald" delay={0} />
        <Kpi tid="kpi-antrian-dicetak-bulan-ini" icon={Printer} label="Tiket Dicetak Bulan Ini" value={fmtNum(b.dicetak || 0)}
             sub={`dari ${fmtNum(b.terbit || 0)} nomor terbit${b.cetak_ulang ? ` · ${fmtNum(b.cetak_ulang)} cetak ulang` : ""}`}
             tint="sky" delay={0.05} />
        <Kpi tid="kpi-antrian-menunggu" icon={Ticket} label="Masih Menunggu Hari Ini" value={fmtNum(h.menunggu || 0)}
             sub={`${fmtNum(h.dipanggil || 0)} sedang dilayani`} tint="amber" delay={0.1} />
        <Kpi tid="kpi-antrian-membeli" icon={ShoppingBag} label="Membeli Bulan Ini" value={fmtNum(b.membeli || 0)}
             sub={`${fmtNum(b.tidak_membeli || 0)} tidak membeli · ${fmtNum(b.tidak_hadir || 0)} tidak hadir`}
             tint="slate" delay={0.15} />
      </div>
      {perluCatatanCetak && (
        <p className="text-[11px] text-slate-500 mb-3" data-testid="dash-antrian-catatan-cetak">
          {sejak
            ? `Catatan cetak baru tersimpan sejak ${formatTanggal(sejak)} — tiket yang terbit sebelumnya terhitung belum dicetak.`
            : "Belum ada tiket yang tercatat dicetak bulan ini. Catatan cetak mulai tersimpan begitu tombol Cetak Tiket ditekan."}
        </p>
      )}
      {perCabang.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-xs text-slate-400" data-testid="dash-antrian-kosong">
          Belum ada nomor antrian terbit bulan ini
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full text-xs md:text-sm" data-testid="dash-antrian-per-cabang">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left font-medium px-3 py-2">Cabang</th>
                <th className="text-right font-medium px-3 py-2">Terbit</th>
                <th className="text-right font-medium px-3 py-2">Dicetak</th>
                <th className="text-right font-medium px-3 py-2">Cetak ulang</th>
                <th className="text-right font-medium px-3 py-2">Membeli</th>
              </tr>
            </thead>
            <tbody>
              {perCabang.map((c, i) => (
                <tr key={c.cabang} className="border-t border-slate-100" data-testid={`dash-antrian-cabang-${i}`}>
                  <td className="px-3 py-2 text-slate-900">{c.cabang}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.terbit)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.dicetak)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.cetak_ulang)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(c.membeli)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Seksi "Tenaga Kerja & Payroll" — G8 uji coba memindahkannya dari halaman
 *  depan ke Dashboard HRIS (`pages/HrisDashboard.jsx`). Isinya TIDAK diubah;
 *  gerbang per widget tetap `canWidget`, dan pemanggil hanya merender
 *  komponen ini kalau minimal satu widget HRIS dicentang. */
export function SeksiHris({ data, canWidget }) {
  return (
      <section data-testid="seksi-hris">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900">Tenaga Kerja &amp; Payroll</h2>
          <span className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">HRIS</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-5 md:mb-6">
          {canWidget("headcount") && (
            <>
              <Kpi tid="kpi-total" icon={Users} label="Total Karyawan" value={fmtNum(data.headcount.total)} delay={0} />
              <Kpi tid="kpi-active" icon={UserCheck} label="Aktif" value={fmtNum(data.headcount.active)} tint="emerald" delay={0.05} />
              <Kpi tid="kpi-resign" icon={UserX} label="Resign" value={fmtNum(data.headcount.resign)} tint="rose" delay={0.1} />
            </>
          )}
          {canWidget("payroll_total") && (
            <Kpi tid="kpi-payroll" icon={Wallet} label="Payroll Tetap / Bulan" value={idr(data.total_payroll)} tint="amber" delay={0.15} />
          )}
        </div>

        {canWidget("headcount") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Komposisi per Branch</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Distribusi karyawan aktif</p>
            {/* Empat chart HRIS di bawah ini (Branch/Gender/Status/Grade) sebelumnya langsung
                menembak data.by_* tanpa penjaga array kosong — beda dari chart Sales di
                section atas yang sudah dijaga (lihat "Metode Pembayaran"/"Omzet per Branch").
                Skenario nyata yang memicu array kosong: scope "own" (portal karyawan yang
                belum tertaut employee aktif) atau perusahaan yang baru saja mengosongkan
                data. Disamakan di sini supaya semua chart di halaman konsisten. */}
            {(data.by_branch || []).length === 0 ? (
              <div className="text-center py-10 text-xs text-slate-400">Belum ada data karyawan</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.by_branch}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#059669" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Komposisi Gender</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Pria vs Wanita</p>
            {(data.by_gender || []).length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400">Belum ada data karyawan</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={data.by_gender} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={4}>
                      {data.by_gender.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {data.by_gender.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-slate-600">{d.name === "L" ? "Pria" : d.name === "P" ? "Wanita" : d.name}</span>
                      </div>
                      <span className="font-medium text-slate-900">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Status Karyawan</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">PKWT vs PKWTT</p>
            {(data.by_status || []).length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400">Belum ada data karyawan</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data.by_status} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={65} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#0F172A" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Top 10 Grade Jabatan</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Grade dengan jumlah karyawan terbanyak</p>
            {(data.by_grade || []).length === 0 ? (
              <div className="text-center py-10 text-xs text-slate-400">Belum ada data karyawan</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.by_grade} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#059669" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
        </div>
        )}
      </section>
  );
}

export default function Dashboard() {
  const { user, can, canWidget } = useAuth();
  const navigate = useNavigate();

  const { data, error: dashError, refetch: muatUlangDashboard } = useDataQuery(
    DASHBOARD_QK,
    () => api.get("/dashboard").then(r => r.data),
  );
  useEffect(() => {
    if (dashError) toast.error(dashError?.response?.data?.detail || "Gagal memuat dashboard.");
  }, [dashError]);

  // Ringkasan keuangan (kas/bank, piutang, utang, aging) memakai endpoint Finance
  // yang sudah ada — bukan query baru. Ditembak HANYA kalau user berizin
  // "finance_dashboard":"view" (`enabled`) — staf non-finance tidak pernah
  // memicu request ke endpoint Finance sama sekali.
  const { data: finBundle, error: finError } = useDataQuery(
    DASHBOARD_FINANCE_QK,
    () => Promise.all([
      api.get("/finance/dashboard/ceo?months=1"),
      api.get("/finance/piutang/aging-buckets"),
    ]).then(([r1, r2]) => ({ fin: r1.data, aging: r2.data })),
    { enabled: can("finance_dashboard", "view") },
  );
  useEffect(() => {
    if (finError) toast.error("Gagal memuat ringkasan keuangan.");
  }, [finError]);
  const fin = finBundle?.fin ?? null;
  const aging = finBundle?.aging ?? null;

  // Panel "Aktivitas Edit & Hapus" (brief bos #22) — sepuluh log terbaru dari
  // /aktivitas. Gerbangnya sama persis dengan blok Ringkasan Keuangan di atas:
  // hanya ditembak kalau berizin "approval_center":"view".
  const { data: aktivitas, error: aktivitasError } = useDataQuery(
    DASHBOARD_AKTIVITAS_QK,
    () => api.get("/aktivitas?page_size=10").then(r => r.data),
    { enabled: can("approval_center", "view") },
  );
  useEffect(() => {
    if (aktivitasError) toast.error("Gagal memuat aktivitas terbaru.");
  }, [aktivitasError]);

  // Tanpa `error`, 403/500 membuat skeleton berputar selamanya (9 dari 16
  // preset izin tidak punya `dashboard.view`). Sekarang pesan + Coba lagi.
  if (!data) return <div className="p-4 md:p-8"><LoadingState loading={!dashError} error={dashError} onRetry={muatUlangDashboard} data-testid="dashboard-loading" /></div>;

  const s = data.sales || {};
  const a = data.acq || {};

  // Group visibility flags (section header hides if no child widget visible)
  const showSalesSection = ["sales_omzet_total", "sales_amount_avg", "sales_today", "acq_mtd", "sales_daily_30d",
                            "sales_by_method", "sales_by_branch", "top_penjual"].some(k => canWidget(k));
  const showHrisSection = ["headcount", "payroll_total"].some(k => canWidget(k));
  // Kartu AI Insight butuh DUA hal: widgetnya diaktifkan DAN akunnya boleh
  // memakai fitur AI (brief owner 30 Agu 2026 — kunci `ai_assistant`, sama
  // dengan gerbang backend `/ai/dashboard-insight`). Dipakai juga di
  // `anyVisible` supaya akun yang cuma punya widget ini tanpa izin AI melihat
  // pesan "belum ada widget", bukan dashboard yang kosong tanpa penjelasan.
  const showAIInsight = canWidget("ai_insights") && bolehPakaiAI(user, can);
  // Antrian dicetak (G7): widget dicentang DAN berizin modul antrian — syarat
  // yang SAMA dengan backend `routers/dashboard.py`. `data.antrian` null kalau
  // backend menolak, jadi tidak pernah muncul kartu "0 antrian" yang menipu.
  const showAntrian = canWidget("antrian_cetak") && (can("antrian", "view") || can("antrian", "update")) && !!data.antrian;
  const anyVisible = showAIInsight || showAntrian || canWidget("wow_sales") || canWidget("wow_attendance")
                     || showSalesSection || showHrisSection || can("finance_dashboard", "view")
                     || can("approval_center", "view");

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-6 md:mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Overview</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Dashboard Sales &amp; POS</h1>
        <p className="mt-2 text-xs md:text-sm text-slate-500">Ringkasan penjualan, akuisisi, antrian, dan keuangan bulan ini.</p>
      </header>

      {/* Cabang akun ini tidak bisa dipetakan ke kosakata cabang penjualan
          (lihat backend routers/dashboard.py — employees.branch vs BRANDS bisa
          beda ejaan di data lama). Tanpa banner ini, angka penjualan nol di
          bawah terlihat seperti "memang sepi bulan ini" padahal sebenarnya
          "belum bisa dipetakan" — dua hal yang sangat berbeda buat pemilik toko. */}
      {data?.scope_cabang_pesan && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="dashboard-cabang-tidak-dikenal">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Angka penjualan/akuisisi belum akurat untuk cabang akun ini</p>
            <p className="text-xs text-amber-700 mt-0.5">{data.scope_cabang_pesan}</p>
          </div>
        </div>
      )}

      {!anyVisible && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="dashboard-empty-state">
          <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-3">
            <EyeOff className="w-6 h-6" />
          </div>
          <h3 className="font-display text-base font-bold text-slate-900 mb-1">Belum ada widget yang diaktifkan</h3>
          <p className="text-sm text-slate-500">Superadmin dapat mengaktifkan widget lewat menu <b>User Management</b> → <b>Atur Izin</b>.</p>
        </div>
      )}

      {/* AI Insight Card */}
      {showAIInsight && <AIInsightCard />}

      {/* Week-over-Week Comparison */}
      {canWidget("wow_sales") && data.wow && <WowCompareCard wow={data.wow} />}

      {/* G8 uji coba: absensi & tenaga kerja pindah ke Dashboard HRIS. Tanpa
          penunjuk ini, orang yang biasa membaca angka karyawan di sini
          mengira angkanya hilang. Syaratnya sama dengan yang dulu merender
          kedua seksi itu di halaman ini. */}
      {(showHrisSection || canWidget("wow_attendance")) && (
        <Link to="/dashboard/hris" data-testid="dash-pindah-hris"
              className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 hover:bg-slate-50">
          <span>Angka tenaga kerja, payroll, dan absensi sekarang ada di <b>Dashboard HRIS</b>.</span>
          <ArrowUpRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        </Link>
      )}

      {/* ---- SALES & POS KPI (Bulan Ini) ---- */}
      {showSalesSection && (
      <section className="mb-8 md:mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900">Ringkasan Penjualan &amp; Akuisisi</h2>
          <span className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">Bulan Ini</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-5 md:mb-6">
          {canWidget("sales_omzet_total") && (
            <Kpi tid="kpi-sales-omzet" icon={TrendingUp} label="Total Omset" value={idr(s.omzet_month || 0)}
                 sub={`${fmtNum(s.count_month || 0)} transaksi`} tint="emerald" delay={0} />
          )}
          {canWidget("sales_today") && (
            <Kpi tid="kpi-sales-today" icon={ShoppingBag} label="Penjualan Hari Ini" value={fmtNum(s.count_today || 0)}
                 sub={idr(s.omzet_today || 0)} tint="sky" delay={0.05} />
          )}
          {canWidget("sales_amount_avg") && (
            <Kpi tid="kpi-sales-avg" icon={Award} label="Amount Penjualan (Rata-rata)" value={idr(s.avg_per_tx || 0)} tint="amber" delay={0.1} />
          )}
          {canWidget("acq_mtd") && (
            <Kpi tid="kpi-acq" icon={Smartphone} label="Akuisisi Unit" value={fmtNum(a.count_month || 0)}
                 sub={idr(a.total_month || 0)} tint="slate" delay={0.15} />
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Trend 30 hari */}
          {canWidget("sales_daily_30d") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Trend Penjualan 30 Hari</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Jumlah transaksi harian &amp; akuisisi unit</p>
            {/* Guard array kosong (mis. scope "own" tanpa transaksi apa pun) — sebelumnya
                AreaChart langsung dikasih data.sales_daily_30d tanpa penjaga, beda dari
                chart Metode Pembayaran/Omzet per Branch di sebelahnya yang sudah dijaga.
                Itu sumber laporan "chart tidak konsisten": sebagian chart di halaman yang
                sama aman terhadap data kosong, sebagian tidak. */}
            {(data.sales_daily_30d || []).length === 0 ? (
              <div className="text-center py-10 text-xs text-slate-400">Belum ada data penjualan 30 hari terakhir</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.sales_daily_30d}>
                  <defs>
                    <linearGradient id="cSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="cAcq" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0F172A" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#0F172A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" name="Penjualan" dataKey="sales" stroke="#059669" strokeWidth={2} fill="url(#cSales)" />
                  <Area type="monotone" name="Akuisisi" dataKey="akuisisi" stroke="#0F172A" strokeWidth={2} fill="url(#cAcq)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </motion.div>
          )}

          {/* Metode Pembayaran Pie */}
          {canWidget("sales_by_method") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Metode Pembayaran</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Distribusi omzet bulan ini</p>
            {(data.sales_by_method || []).length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400">Belum ada penjualan</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={data.sales_by_method} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={3}>
                      {data.sales_by_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => idr(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 space-y-1.5">
                  {data.sales_by_method.slice(0, 5).map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-slate-600 truncate">{d.name}</span>
                      </div>
                      <span className="font-medium text-slate-900 whitespace-nowrap">{idr(d.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
          )}

          {/* Omzet per Branch */}
          {canWidget("sales_by_branch") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Omzet per Branch</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3 md:mb-4">Bulan ini · omzet &amp; transaksi</p>
            {(data.sales_by_branch || []).length === 0 ? (
              <div className="text-center py-10 text-xs text-slate-400">Belum ada penjualan</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.sales_by_branch}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1e6 ? `${(v/1e6).toFixed(1)}jt` : v} />
                  <Tooltip formatter={(v, k) => k === "omzet" ? idr(v) : v} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="omzet" fill="#059669" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
          )}

          {/* Top 5 Penjual */}
          {canWidget("top_penjual") && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Top 5 Penjual</h3>
            <p className="text-[11px] md:text-xs text-slate-500 mt-0.5 mb-3">Omzet bulan ini</p>
            {(data.top_penjual || []).length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-400">Belum ada penjualan</div>
            ) : (
              <div className="space-y-2">
                {data.top_penjual.map((p, i) => (
                  <div key={p.nama} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`top-penjual-${i}`}>
                    <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${
                      i === 0 ? "bg-amber-100 text-amber-700" :
                      i === 1 ? "bg-slate-200 text-slate-700" :
                      i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"
                    }`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm font-medium text-slate-900 truncate">{p.nama}</p>
                      <p className="text-[10px] md:text-[11px] text-slate-500">{p.transaksi} transaksi</p>
                    </div>
                    <p className="font-mono text-[11px] md:text-xs font-semibold text-emerald-700 whitespace-nowrap">{idr(p.omzet)}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
          )}
        </div>
      </section>
      )}

      {/* ---- ANTRIAN PELANGGAN (G7) ---- */}
      {/* Tautan "Buka Antrian" memakai syarat menu Antrian (view ATAU create);
          supervisor yang cuma punya `update` melihat laporannya tanpa tautan
          yang ujungnya tab kosong. */}
      {showAntrian && <SeksiAntrian antrian={data.antrian} bolehBuka={can("antrian", "view") || can("antrian", "create")} />}

      {/* ---- RINGKASAN KEUANGAN (kas/bank, piutang, utang, aging) ---- */}
      {/* Gerbang izin: tidak sekadar disembunyikan lewat CSS — request ke endpoint
          Finance juga tidak pernah dikirim kalau `can` bernilai false (lihat useEffect
          di atas), jadi staf non-finance tidak pernah memicu 403 dari backend. */}
      {can("finance_dashboard", "view") && fin && (
      <section className="mb-8 md:mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900">Ringkasan Keuangan</h2>
          <span className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-medium">Live</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5 mb-5 md:mb-6">
          <Kpi tid="dash-kas-total" icon={Wallet} label="Saldo Kas & Bank" value={idr(fin.cash?.total_cash || 0)}
               sub={`Bank ${idr(fin.cash?.total_bank || 0)} · Kas ${idr(fin.cash?.total_kas || 0)} · Petty ${idr(fin.cash?.total_petty || 0)}`}
               tint="emerald" delay={0} />
          <Kpi tid="dash-piutang-total" icon={HandCoins} label="Total Piutang" value={idr(fin.ar_ap?.total_ar || 0)}
               sub={`Overdue: ${idr(fin.ar_ap?.overdue_ar || 0)} (${fin.ar_ap?.ar_overdue_pct || 0}%)`}
               tint="amber" delay={0.05} />
          <Kpi tid="dash-utang-total" icon={Receipt} label="Total Utang" value={idr(fin.ar_ap?.total_ap || 0)}
               sub={`Jatuh tempo 7 hari: ${idr(fin.ar_ap?.ap_due_7d || 0)}`}
               tint="rose" delay={0.1} />
        </div>

        {aging && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            data-testid="dash-aging-panel"
            className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-semibold text-slate-900 text-sm md:text-base">Aging Piutang</h3>
              <span className="text-[11px] text-slate-500">Per {aging.as_of}</span>
            </div>
            <p className="text-[11px] md:text-xs text-slate-500 mb-4">
              {aging.count_total} piutang aktif · Total {idr(aging.total_aktif)}
            </p>
            {aging.buckets.every(b => b.total === 0) ? (
              <div className="text-center py-8 text-xs text-slate-400">Belum ada piutang aktif</div>
            ) : (
              <div className="space-y-3">
                {aging.buckets.map(b => {
                  const overdue = ["31_60", "61_90", "over_90"].includes(b.key);
                  return (
                    <div key={b.key} data-testid={`dash-aging-bucket-${b.key}`}>
                      <div className="flex items-center justify-between mb-1 text-xs">
                        <span className={`font-medium ${overdue ? "text-rose-700" : "text-slate-700"}`}>{b.label}</span>
                        <span className="text-slate-500">
                          {b.count} dok · <span className="font-mono font-semibold text-slate-900">{idr(b.total)}</span>
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${b.pct}%`, backgroundColor: b.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </section>
      )}

      {/* ---- AKTIVITAS EDIT & HAPUS (brief bos #22) ---- */}
      {/* Gerbang izin sama seperti Ringkasan Keuangan: request ke /aktivitas tidak
          pernah dikirim kalau `can` bernilai false (lihat useEffect di atas). */}
      {can("approval_center", "view") && aktivitas && (
      <section className="mb-8 md:mb-10" data-testid="dash-aktivitas-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-base md:text-lg font-bold text-slate-900 inline-flex items-center gap-2">
            <History className="w-5 h-5 text-slate-500" />
            Aktivitas Edit &amp; Hapus
          </h2>
          <Link to="/approval-center" data-testid="dash-aktivitas-link-approval-center"
            className="text-[11px] md:text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-slate-200">
            Pusat Approval →
          </Link>
        </div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {(aktivitas.items || []).length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-400">Belum ada aktivitas edit/hapus tercatat</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs md:text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Kapan</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Siapa</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Aksi</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500 whitespace-nowrap">Modul</th>
                    <th className="text-left px-4 py-2 font-semibold text-slate-500">Ringkasan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {aktivitas.items.map((it) => {
                    // Tautan ke modul terkait (brief bos #9) — path dari peta eksplisit
                    // modul→rute (lib/rutaAktivitas.js), digerbangi izin halaman TUJUAN
                    // (bisa beda dari `it.modul` sendiri, lihat kasus my_kpi di berkas itu).
                    // Modul yang tidak ada di peta / user tidak berhak → baris tetap baris
                    // biasa, tidak bisa diklik (bukan tautan yang berujung 403/404).
                    const tujuan = rutaUntukAktivitas(it);
                    const bisaKlik = Boolean(tujuan) && can(tujuan.permModul, "view");
                    return (
                      <tr
                        key={it.id}
                        data-testid={`dash-aktivitas-row-${it.id}`}
                        onClick={bisaKlik ? () => navigate(tujuan.to) : undefined}
                        onKeyDown={bisaKlik ? (e) => {
                          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(tujuan.to); }
                        } : undefined}
                        role={bisaKlik ? "link" : undefined}
                        tabIndex={bisaKlik ? 0 : undefined}
                        title={bisaKlik ? "Buka halaman modul terkait" : undefined}
                        className={bisaKlik ? "cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" : undefined}
                      >
                        <td className="px-4 py-2 whitespace-nowrap text-slate-500">{fmtWaktuAktivitas(it.at)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700">{it.oleh_nama || it.oleh || "-"}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            it.aksi === "hapus" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
                          }`}>
                            {it.aksi === "hapus" ? "Hapus" : "Edit"}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-600">{it.modul}</td>
                        <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={it.ringkasan}>{it.ringkasan || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </section>
      )}
    </div>
  );
}
